import { getIdToken } from 'firebase/auth'
import { auth } from '@/firebase'
import { formatDuration } from '@/utils/format'
import {
  getFallbackCategory,
  getFallbackMaterialPrice
} from '@/assets/material/materialCatalog'
import { enabledOnly, sortByOrder, sortMaterialItems } from '@/utils/materialSort'

function localize(data, t) {
  const locale = t?.locale || ''
  const useChinese = locale.toLowerCase().startsWith('zh')
  const title = useChinese ? data.titleZh || data.title : data.titleEn || data.title
  const description = useChinese ? data.descriptionZh || data.description : data.descriptionEn || data.description
  return {
    ...data,
    title,
    description
  }
}

function normalizeCategory(data, t) {
  const fallback = getFallbackCategory(data.id, t) || {}
  const localized = localize(data, t)
  return {
    ...localized,
    image: fallback.image || localized.image || localized.coverUrl,
    defaultPrice: localized.defaultPrice || 50
  }
}

function normalizeCollection(data, categoryId, category, t) {
  const localized = localize(data, t)
  return {
    ...localized,
    id: data.id,
    categoryId,
    title: localized.title || data.titleEn || data.titleZh || data.id,
    coverUrl: data.coverUrl || data.image,
    price: data.price || category?.defaultPrice
  }
}

function normalizeMaterial(data, categoryId, collectionId, collectionData = null, categoryData = null) {
  const durationSeconds = data.durationSeconds ?? data.duration
  const filePath = data.audioFilePath || data.filePath
  const youtubeVideoId = data.youtubeVideoId || data.youtube?.videoId || ''
  const fileName = data.name || filePath?.split('/').pop() || ''
  const categoryDefaults = getFallbackCategory(categoryId) || {}
  const categoryImage = categoryDefaults.image || categoryData?.image || categoryData?.coverUrl
  const type = !data.type || data.type === 'transcription' ? 'stt' : data.type
  const fallbackTitle = fileName
    ? fileName.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ')
    : data.id
  return {
    ...data,
    id: data.id,
    categoryId,
    collectionId,
    type,
    image: data.coverUrl || data.image || collectionData?.coverUrl || collectionData?.image || categoryImage,
    title: data.title || data.titleEn || data.titleZh || fallbackTitle,
    description: data.description || data.descriptionEn || data.descriptionZh,
    filePath,
    youtubeVideoId,
    mediaType: youtubeVideoId || data.sourceType === 'youtube' ? 'video' : 'audio',
    durationSeconds,
    duration: data.durationText || formatDuration(durationSeconds, '0 min'),
    price: data.price || getFallbackMaterialPrice(categoryId, collectionId, data)
  }
}

const requestCache = {
  categories: null,
  collections: new Map(),
  items: new Map(),
  details: new Map()
}

export function clearMaterialCache() {
  requestCache.categories = null
  requestCache.collections.clear()
  requestCache.items.clear()
  requestCache.details.clear()
}

function buildQueryString(params = {}) {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value))
    }
  })
  const queryString = search.toString()
  return queryString ? `?${queryString}` : ''
}

async function readMaterialApi(path, params = {}, options = {}) {
  const headers = {}
  if (options.auth) {
    if (!auth.currentUser) throw new Error('User not authenticated')
    headers.Authorization = `Bearer ${await getIdToken(auth.currentUser)}`
  }

  const response = await fetch(`/api/materials/${path}${buildQueryString(params)}`, { headers })
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`
    try {
      const errorData = await response.json()
      errorMessage = errorData.error || errorData.message || errorMessage
    } catch {
      // Keep default message when response is not JSON.
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

function cachedRequest(cache, key, loader) {
  if (!cache.has(key)) {
    cache.set(key, loader().catch(error => {
      cache.delete(key)
      throw error
    }))
  }
  return cache.get(key)
}

function readCachedCategories() {
  if (!requestCache.categories) {
    requestCache.categories = readMaterialApi('categories')
      .then(data => data.categories || [])
      .catch(error => {
        requestCache.categories = null
        throw error
      })
  }
  return requestCache.categories
}

function readCachedCollections(categoryId) {
  return cachedRequest(requestCache.collections, categoryId, () => (
    readMaterialApi('collections', { categoryId }).then(data => data.collections || [])
  ))
}

function readCachedItems(categoryId, collectionId) {
  const key = `${categoryId}/${collectionId}`
  return cachedRequest(requestCache.items, key, () => (
    readMaterialApi('items', { categoryId, collectionId }).then(data => data.items || [])
  ))
}

function detailCacheKey(categoryId, collectionId, itemId) {
  const userKey = auth.currentUser?.uid || 'anonymous'
  return `${userKey}/${categoryId}/${collectionId}/${itemId}`
}

function readCachedDetail(categoryId, collectionId, itemId) {
  return cachedRequest(requestCache.details, detailCacheKey(categoryId, collectionId, itemId), () => (
    readMaterialApi('detail', { categoryId, collectionId, itemId }, { auth: true }).then(data => data.item)
  ))
}

// 购买接口已带回详情，存进缓存，打开素材时不用再请求
function rememberMaterialDetail({ categoryId, collectionId, itemId, item }) {
  requestCache.details.set(detailCacheKey(categoryId, collectionId, itemId), Promise.resolve(item))
}

export function useMaterials(t) {
  async function fetchMaterialCategories() {
    const remoteCategories = await readCachedCategories()
    return remoteCategories
      .filter(enabledOnly)
      .map(category => normalizeCategory(category, t))
      .sort(sortByOrder)
  }

  async function fetchMaterialCategory(categoryId) {
    const categories = await fetchMaterialCategories()
    return categories.find(category => category.id === categoryId) || getFallbackCategory(categoryId, t)
  }

  async function fetchMaterialCollections(categoryId) {
    const category = await fetchMaterialCategory(categoryId)

    const remoteCollections = await readCachedCollections(categoryId)
    return remoteCollections
      .filter(enabledOnly)
      .map(entry => normalizeCollection(entry, categoryId, category, t))
      .sort(sortByOrder)
  }

  async function fetchMaterialItems({ categoryId, collectionId }) {
    if (!categoryId || !collectionId) return []

    const remoteItems = await readCachedItems(categoryId, collectionId)
    const category = await fetchMaterialCategory(categoryId)
    const collections = await fetchMaterialCollections(categoryId)
    const collectionData = collections.find(collection => collection.id === collectionId)
    const normalizedItems = remoteItems
      .filter(enabledOnly)
      .map(item => normalizeMaterial(item, categoryId, collectionId, collectionData, category))
    return sortMaterialItems(normalizedItems)
  }

  async function fetchMaterialDetail({ categoryId, collectionId, item }) {
    if (item?.filePath && (
      item.subtitles?.length
      || item.practiceSubtitles?.length
      || item.originalSubtitles?.length
      || item.completeSubtitles?.length
    )) {
      return item
    }

    const matchingItem = await readCachedDetail(categoryId, collectionId, item.id)
    const category = await fetchMaterialCategory(categoryId)
    const collections = await fetchMaterialCollections(categoryId)
    const collectionData = collections.find(collection => collection.id === collectionId)
    return normalizeMaterial(matchingItem, categoryId, collectionId, collectionData, category)
  }

  function getPurchaseKey(categoryId, itemId) {
    return `${categoryId}-${itemId}`
  }

  function getMaterialPrice(categoryId, collectionId, item = {}) {
    return item.price || getFallbackMaterialPrice(categoryId, collectionId, item)
  }

  return {
    fetchMaterialCategories,
    fetchMaterialCategory,
    fetchMaterialCollections,
    fetchMaterialItems,
    fetchMaterialDetail,
    rememberMaterialDetail,
    getPurchaseKey,
    getMaterialPrice
  }
}
